import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Edit3, Camera, User as UserIcon } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Spinner } from "../ui/FullScreenLoader";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const editProfileSchema = z.object({
  name: z.string().trim().min(2).max(100),
  email: z.string().email(),
  phone_number: z.string().optional().nullable(),
  password: z.string().min(8).max(72).optional().or(z.literal("")),
});
type EditProfileForm = z.infer<typeof editProfileSchema>;

function formatPhoneNumber(val: string) {
  if (!val) return "";
  const digits = val.replace(/\D/g, "");
  let core = digits;
  if (core.startsWith("1")) {
    core = core.slice(1);
  }
  core = core.slice(0, 10);
  if (core.length === 0) return "";
  if (core.length <= 3) return `1(${core}`;
  if (core.length <= 6) return `1(${core.slice(0, 3)})-${core.slice(3)}`;
  return `1(${core.slice(0, 3)})-${core.slice(3, 6)}-${core.slice(6)}`;
}

export function EditProfileModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { user, refresh } = useAuth();
  
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(user?.avatarUrl || null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EditProfileForm>({
    resolver: zodResolver(editProfileSchema),
    defaultValues: user
      ? {
          name: user.name,
          email: user.email,
          phone_number: user.phone_number || "",
          password: "",
        }
      : undefined,
  });

  const save = useMutation({
    mutationFn: async (data: EditProfileForm) => {
      if (!user) throw new Error("Not logged in");
      
      const payload: Record<string, unknown> = { ...data };
      if (!payload.password) delete payload.password;
      
      const res = (await api.patch(`/api/users/${encodeURIComponent(user.id)}`, payload)).data;
      
      if (avatarFile) {
        const fd = new FormData();
        fd.append("avatar", avatarFile);
        await api.post(`/api/users/${encodeURIComponent(user.id)}/avatar`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      return res;
    },
    onSuccess: async () => {
      await refresh(); // Refresh auth context so topbar/sidebar updates
      qc.invalidateQueries({ queryKey: ["admin.users"] });
      toast.success("Profile updated");
      setAvatarFile(null);
      onClose();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  if (!user) return null;

  return (
    <Modal open={open} onClose={onClose} title="Edit Profile">
      <form
        onSubmit={handleSubmit((d) => save.mutate(d))}
        className="space-y-3"
      >
        <div className="flex flex-col items-center justify-center pt-2 pb-4 relative">
          <label htmlFor="avatar-upload-profile" className="w-24 h-24 rounded-full bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden mb-2 relative group cursor-pointer shadow-glass-sm hover:border-brand-500/50 transition-colors">
            {previewUrl ? (
              <img src={previewUrl} alt="Profile preview" className="w-full h-full object-cover" />
            ) : (
              <UserIcon className="w-10 h-10 text-slate-400 group-hover:text-brand-300 transition-colors" />
            )}
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-[2px]">
               <Camera className="w-6 h-6 text-white" />
            </div>
          </label>
          <input
            id="avatar-upload-profile"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setAvatarFile(file);
                setPreviewUrl(URL.createObjectURL(file));
              }
            }}
          />
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Profile Picture</span>
        </div>

        <div>
          <label className="label">Name</label>
          <input className="input" {...register("name")} />
          {errors.name && <p className="field-error">{errors.name.message}</p>}
        </div>
        
        <div>
          <label className="label">Email</label>
          <input type="email" className="input" {...register("email")} />
          {errors.email && <p className="field-error">{errors.email.message}</p>}
        </div>

        <div>
          <label className="label">Phone Number</label>
          <input
            className="input"
            placeholder="1(000)-000-0000"
            {...register("phone_number", {
              onChange: (e) => {
                e.target.value = formatPhoneNumber(e.target.value);
              },
            })}
          />
        </div>
        
        <div>
          <label className="label">New password (optional)</label>
          <input
            type="password"
            className="input"
            placeholder="Leave blank to keep current"
            {...register("password")}
          />
          {errors.password && (
            <p className="field-error">{errors.password.message}</p>
          )}
        </div>

        <div className="pt-3 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? <Spinner /> : <Edit3 className="w-4 h-4" />}
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}
